import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Camera, User, Building2, Phone, Mail, Save, Settings, Moon, Sun, MessageSquare, MapPin, Plus, Edit, Trash2, Clock, Bell, FileText, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
// import { toast } from '../hooks/use-toast'; // Disabled to avoid portal errors

// Simple toast replacement
const toast = ({ title, description, variant }) => {
  console.log(`[${variant === 'destructive' ? 'ERROR' : 'INFO'}] ${title}: ${description}`);
};
import { STORAGE_KEYS } from '../mock';
import { branchesAPI, servicesAPI, profileAPI } from '../services/api';

const UserProfile = ({ onClose }) => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    photoUrl: '',
    speciality: ''
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'settings', 'branches', 'services'
  
  // Branches (Sucursales) states - now from API
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchForm, setBranchForm] = useState({
    id: '',
    name: '',
    address: '',
    gpsLocation: '',
    timezone: 'GMT-06:00',
    color: '#3B82F6'
  });

  // Services states - from API
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    id: '',
    name: '',
    description: '',
    duration: '',
    color: '#10B981'
  });
  
  // Settings states
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [selectedBranchForSettings, setSelectedBranchForSettings] = useState(null);
  const [branchTemplates, setBranchTemplates] = useState(() => {
    const saved = localStorage.getItem('branchTemplates');
    return saved ? JSON.parse(saved) : {};
  });
  const [messageTemplate, setMessageTemplate] = useState('');
  const [includeDate, setIncludeDate] = useState(true);
  const [includeTime, setIncludeTime] = useState(true);
  const [includeService, setIncludeService] = useState(true);
  const [includeBranch, setIncludeBranch] = useState(false);
  
  // Auto-reminder settings
  const [autoReminders, setAutoReminders] = useState({
    enabled24h: true,
    time24h: '21:00',
    enabled12h: false,
    enabledSameDay: true,
    timeSameDay: '07:30'
  });

  // Confirmation message settings
  const [confirmationTemplate, setConfirmationTemplate] = useState('');
  const [confirmIncludeBusiness, setConfirmIncludeBusiness] = useState(true);
  const [confirmIncludeSpecialty, setConfirmIncludeSpecialty] = useState(true);
  const [confirmIncludeService, setConfirmIncludeService] = useState(true);
  const [confirmIncludeAddress, setConfirmIncludeAddress] = useState(true);
  const [confirmIncludeReminderNote, setConfirmIncludeReminderNote] = useState(true);

  // Reminder message settings
  const [reminderTemplate, setReminderTemplate] = useState('');
  const [thanksTemplate, setThanksTemplate] = useState('');
  const [thanksIncludeSocial, setThanksIncludeSocial] = useState(true);
  const [thanksIncludeReview, setThanksIncludeReview] = useState(true);
  const [socialLinks, setSocialLinks] = useState({ facebook: '', instagram: '', whatsapp: '' });
  const [reviewLink, setReviewLink] = useState('');
  const [reminderIncludeServiceDetails, setReminderIncludeServiceDetails] = useState(true);

  useEffect(() => {
    loadProfile();
    loadBranches();
    loadServices();
    // Apply dark mode on load
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [currentUser, darkMode]);

  const loadBranches = async () => {
    try {
      setLoadingBranches(true);
      const response = await branchesAPI.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las sucursales',
        variant: 'destructive',
      });
    } finally {
      setLoadingBranches(false);
    }
  };

  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      // Don't show error toast as services are optional
    } finally {
      setLoadingServices(false);
    }
  };

  // Load branch template when branch is selected
  useEffect(() => {
    if (selectedBranchForSettings) {
      const template = branchTemplates[selectedBranchForSettings];
      if (template) {
        setMessageTemplate(template.message || '');
        setIncludeDate(template.includeDate !== false);
        setIncludeTime(template.includeTime !== false);
        setIncludeService(template.includeService !== false);
        setIncludeBranch(template.includeBranch === true);
      } else {
        // Default template for new branch
        setMessageTemplate('¡Hola {nombre}! 👋\n\nTe recordamos tu cita:\n\n{fecha}\n{hora}\n{servicio}\n\nPor favor confirma tu asistencia.\n\n¡Te esperamos!');
        setIncludeDate(true);
        setIncludeTime(true);
        setIncludeService(true);
        setIncludeBranch(false);
      }
    }
  }, [selectedBranchForSettings]);

  const loadProfile = async () => {
    try {
      // Clean up any old localStorage profile data to prevent conflicts
      const oldProfileKey = `${STORAGE_KEYS.CURRENT_USER}_profile`;
      if (localStorage.getItem(oldProfileKey)) {
        console.log('[UserProfile] Cleaning up old localStorage profile data');
        localStorage.removeItem(oldProfileKey);
      }
      
      // Load profile from backend
      console.log('[UserProfile] Loading profile from backend API...');
      const response = await profileAPI.get();
      const savedProfile = response.data;
      console.log('[UserProfile] Profile loaded successfully:', {
        name: savedProfile.name,
        businessName: savedProfile.businessName,
        phone: savedProfile.phone
      });
      
      setProfile({
        name: savedProfile.name || currentUser?.name || '',
        businessName: savedProfile.businessName || '',
        email: savedProfile.email || currentUser?.email || '',
        phone: savedProfile.phone || '',
        photoUrl: savedProfile.photoUrl || '',
        speciality: savedProfile.speciality || ''
      });
      setPhotoPreview(savedProfile.photoUrl);
      
      // Load confirmation template settings
      if (savedProfile.confirmationTemplate) {
        setConfirmationTemplate(savedProfile.confirmationTemplate);
      } else {
        // Default confirmation template with service details
        setConfirmationTemplate('¡Hola {nombre}! 👋\n\nTu cita ha sido agendada exitosamente:\n\n📅 Fecha: {fecha}\n🕐 Hora: {hora}\n📋 Servicio: {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\n{recordatorio_nota}\n\n¡Te esperamos!');
      }
      
      // Load reminder template settings
      if (savedProfile.reminderTemplate) {
        setReminderTemplate(savedProfile.reminderTemplate);
      } else {
        // Default reminder template
        setReminderTemplate('¡Hola {nombre}! 👋\n\nTe recordamos tu cita:\n\n📅 {fecha}\n🕐 {hora}\n📋 {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\nPor favor confirma tu asistencia usando los enlaces a continuación.\n\n¡Te esperamos!');
      }
      
      setConfirmIncludeBusiness(savedProfile.confirmationIncludeBusiness !== false);
      setConfirmIncludeSpecialty(savedProfile.confirmationIncludeSpecialty !== false);
      setConfirmIncludeService(savedProfile.confirmationIncludeService !== false);
      setConfirmIncludeAddress(savedProfile.confirmationIncludeAddress !== false);
      setConfirmIncludeReminderNote(savedProfile.confirmationIncludeReminderNote !== false);
      
      setReminderIncludeServiceDetails(savedProfile.reminderIncludeServiceDetails !== false);
      
      // Load thanks template settings
      if (savedProfile.thanksTemplate) {
        setThanksTemplate(savedProfile.thanksTemplate);
      } else {
        // Default thanks template
        setThanksTemplate('¡Gracias por tu visita, {nombre}! 🙏\n\nEsperamos que hayas tenido una excelente experiencia.\n\n{redes_sociales}\n\n{link_calificacion}\n\n¡Esperamos verte pronto!');
      }
      
      setThanksIncludeSocial(savedProfile.thanksIncludeSocial !== false);
      setThanksIncludeReview(savedProfile.thanksIncludeReview !== false);
      setSocialLinks(savedProfile.socialLinks || { facebook: '', instagram: '', whatsapp: '' });
      setReviewLink(savedProfile.reviewLink || '');
      
      // Load auto-reminders settings
      if (savedProfile.autoReminders) {
        setAutoReminders(savedProfile.autoReminders);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Set defaults if profile doesn't exist yet
      setProfile(prev => ({ ...prev, name: currentUser?.name || '', email: currentUser?.email || '' }));
      // Set default confirmation template with service details
      setConfirmationTemplate('¡Hola {nombre}! 👋\n\nTu cita ha sido agendada exitosamente:\n\n📅 Fecha: {fecha}\n🕐 Hora: {hora}\n📋 Servicio: {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\n{recordatorio_nota}\n\n¡Te esperamos!');
      
      // Set default reminder template
      setReminderTemplate('¡Hola {nombre}! 👋\n\nTe recordamos tu cita:\n\n📅 {fecha}\n🕐 {hora}\n📋 {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\nPor favor confirma tu asistencia usando los enlaces a continuación.\n\n¡Te esperamos!');
      
      // Set default thanks template
      setThanksTemplate('¡Gracias por tu visita, {nombre}! 🙏\n\nEsperamos que hayas tenido una excelente experiencia.\n\n{redes_sociales}\n\n{link_calificacion}\n\n¡Esperamos verte pronto!');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'La imagen debe ser menor a 5MB',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setProfile(prev => ({ ...prev, photoUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleToggleDarkMode = (enabled) => {
    setDarkMode(enabled);
    localStorage.setItem('darkMode', enabled.toString());
    
    if (enabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    toast({
      title: enabled ? 'Tema oscuro activado' : 'Tema claro activado',
      description: 'Los cambios se aplicarán en toda la aplicación',
    });
  };

  const handleSaveSettings = () => {
    if (!selectedBranchForSettings) {
      toast({
        title: 'Selecciona una sucursal',
        description: 'Debes seleccionar una sucursal para guardar la configuración',
        variant: 'destructive',
      });
      return;
    }

    const updatedTemplates = {
      ...branchTemplates,
      [selectedBranchForSettings]: {
        message: messageTemplate,
        includeDate,
        includeTime,
        includeService,
        includeBranch
      }
    };

    setBranchTemplates(updatedTemplates);
    localStorage.setItem('branchTemplates', JSON.stringify(updatedTemplates));
    
    toast({
      title: 'Configuración guardada',
      description: 'La plantilla de esta sucursal se ha guardado correctamente',
    });
  };

  const handleSaveAutoReminders = () => {
    localStorage.setItem('autoReminders', JSON.stringify(autoReminders));
    toast({
      title: 'Recordatorios automáticos configurados',
      description: 'Se guardarán tus preferencias de horarios',
    });
  };

  const handleSaveConfirmationTemplate = async () => {
    try {
      const updatedProfile = {
        ...profile,
        confirmationTemplate,
        confirmationIncludeBusiness: confirmIncludeBusiness,
        confirmationIncludeSpecialty: confirmIncludeSpecialty,
        confirmationIncludeService: confirmIncludeService,
        confirmationIncludeAddress: confirmIncludeAddress,
        confirmationIncludeReminderNote: confirmIncludeReminderNote
      };
      
      // Save to backend API
      await profileAPI.update(updatedProfile);
      setProfile(updatedProfile);
      
      toast({
        title: 'Plantilla guardada',
        description: 'La plantilla de confirmación se guardó correctamente y sincronizada en todos tus dispositivos',
      });
    } catch (error) {
      console.error('Error saving confirmation template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la plantilla. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const getConfirmationPreview = () => {
    let message = confirmationTemplate;
    
    // Replace patient name (only first name)
    const fullName = 'María González';
    const firstName = fullName.split(' ')[0];
    message = message.replace('{nombre}', firstName);
    
    // Replace date and time
    message = message.replace('{fecha}', '20 de octubre de 2025');
    message = message.replace('{hora}', '2:30 PM');
    
    // Service type and details
    let serviceText = '';
    let serviceDuration = '';
    let serviceDescription = '';
    
    if (confirmIncludeService && services.length > 0) {
      const exampleService = services[0];
      serviceText = exampleService.name;
      serviceDuration = exampleService.duration ? `⏱️ ${exampleService.duration}` : '';
      serviceDescription = exampleService.description || '';
    } else if (confirmIncludeService) {
      serviceText = 'Consulta General';
      serviceDuration = '⏱️ 45 minutos';
      serviceDescription = 'Revisión médica general';
    }
    
    message = message.replace('{servicio}', serviceText);
    message = message.replace('{servicio_duracion}', serviceDuration);
    message = message.replace('{servicio_descripcion}', serviceDescription);
    
    // Business name and specialty
    let businessSpecialtyText = '';
    if (confirmIncludeBusiness && profile.businessName) {
      businessSpecialtyText += `📍 ${profile.businessName}`;
    }
    if (confirmIncludeSpecialty && profile.speciality) {
      businessSpecialtyText += confirmIncludeBusiness && profile.businessName ? ` - ${profile.speciality}` : `👨‍⚕️ ${profile.speciality}`;
    }
    message = message.replace('{negocio_especialidad}', businessSpecialtyText);
    
    // Address (use first branch if available)
    let addressText = '';
    if (confirmIncludeAddress && branches.length > 0) {
      const firstBranch = branches[0];
      addressText = `📍 Ubicación: ${firstBranch.name}\n${firstBranch.address || ''}`;
      
      // Add Google Maps link if GPS location is available
      if (firstBranch.gpsLocation) {
        addressText += `\n🗺️ Ver en mapa: https://www.google.com/maps?q=${encodeURIComponent(firstBranch.gpsLocation)}`;
      }
    }
    message = message.replace('{direccion}', addressText);
    
    // Reminder note
    let reminderNoteText = '';
    if (confirmIncludeReminderNote) {
      reminderNoteText = '📬 Recibirás un recordatorio antes de tu cita.';
    }
    message = message.replace('{recordatorio_nota}', reminderNoteText);
    
    // Clean up multiple empty lines
    message = message.replace(/\n\n\n+/g, '\n\n');
    
    return message.trim();
  };

  const handleSaveReminderTemplate = async () => {
    try {
      const updatedProfile = {
        ...profile,
        reminderTemplate,
        reminderIncludeServiceDetails
      };
      
      // Save to backend API
      await profileAPI.update(updatedProfile);
      setProfile(updatedProfile);
      
      toast({
        title: 'Plantilla guardada',
        description: 'La plantilla de recordatorio se guardó correctamente y sincronizada en todos tus dispositivos',
      });
    } catch (error) {
      console.error('Error saving reminder template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la plantilla. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveThanksTemplate = async () => {
    try {
      const updatedProfile = {
        ...profile,
        thanksTemplate,
        thanksIncludeSocial,
        thanksIncludeReview,
        socialLinks,
        reviewLink
      };
      
      // Save to backend API
      await profileAPI.update(updatedProfile);
      setProfile(updatedProfile);
      
      toast({
        title: 'Plantilla guardada',
        description: 'La plantilla de agradecimiento se guardó correctamente y sincronizada en todos tus dispositivos',
      });
    } catch (error) {
      console.error('Error saving thanks template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la plantilla. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const getThanksPreview = () => {
    let message = thanksTemplate;
    
    // Replace patient name (only first name)
    const fullName = 'María González';
    const firstName = fullName.split(' ')[0];
    message = message.replace('{nombre}', firstName);
    
    // Social links
    let socialText = '';
    if (thanksIncludeSocial) {
      const links = [];
      if (socialLinks.facebook) links.push(`📘 Facebook: ${socialLinks.facebook}`);
      if (socialLinks.instagram) links.push(`📸 Instagram: ${socialLinks.instagram}`);
      if (socialLinks.whatsapp) links.push(`💬 WhatsApp: ${socialLinks.whatsapp}`);
      if (links.length > 0) {
        socialText = '🌐 Síguenos en redes sociales:\n' + links.join('\n');
      }
    }
    message = message.replace('{redes_sociales}', socialText);
    
    // Review link
    let reviewText = '';
    if (thanksIncludeReview && reviewLink) {
      reviewText = `⭐ Ayúdanos con tu opinión:\n${reviewLink}`;
    }
    message = message.replace('{link_calificacion}', reviewText);
    
    // Clean up multiple empty lines
    message = message.replace(/\n\n\n+/g, '\n\n');
    
    return message.trim();
  };

  const getReminderPreview = () => {
    let message = reminderTemplate;
    
    // Replace patient name (only first name)
    const fullName = 'María González';
    const firstName = fullName.split(' ')[0];
    message = message.replace('{nombre}', firstName);
    
    // Replace date and time
    message = message.replace('{fecha}', '20 de octubre de 2025');
    message = message.replace('{hora}', '2:30 PM');
    
    // Service type and details
    let serviceText = '';
    let serviceDuration = '';
    let serviceDescription = '';
    
    if (reminderIncludeServiceDetails && services.length > 0) {
      const exampleService = services[0];
      serviceText = exampleService.name;
      serviceDuration = exampleService.duration ? `⏱️ ${exampleService.duration}` : '';
      serviceDescription = exampleService.description || '';
    } else if (reminderIncludeServiceDetails) {
      serviceText = 'Consulta General';
      serviceDuration = '⏱️ 45 minutos';
      serviceDescription = 'Revisión médica general';
    }
    
    message = message.replace('{servicio}', serviceText);
    message = message.replace('{servicio_duracion}', serviceDuration);
    message = message.replace('{servicio_descripcion}', serviceDescription);
    
    // Business name and specialty
    let businessSpecialtyText = '';
    if (profile.businessName) {
      businessSpecialtyText += `📍 ${profile.businessName}`;
    }
    if (profile.speciality) {
      businessSpecialtyText += profile.businessName ? ` - ${profile.speciality}` : `👨‍⚕️ ${profile.speciality}`;
    }
    message = message.replace('{negocio_especialidad}', businessSpecialtyText);
    
    // Address (use first branch if available)
    let addressText = '';
    if (branches.length > 0) {
      const firstBranch = branches[0];
      addressText = `📍 Ubicación: ${firstBranch.name}\n${firstBranch.address || ''}`;
      
      // Add Google Maps link if GPS location is available
      if (firstBranch.gpsLocation) {
        addressText += `\n🗺️ Ver en mapa: https://www.google.com/maps?q=${encodeURIComponent(firstBranch.gpsLocation)}`;
      }
    }
    message = message.replace('{direccion}', addressText);
    
    // Clean up multiple empty lines
    message = message.replace(/\n\n\n+/g, '\n\n');
    
    return message.trim();
  };

  const getPreviewMessage = () => {
    if (!selectedBranchForSettings) {
      return 'Selecciona una sucursal para ver la vista previa...';
    }

    const selectedBranch = branches.find(b => b.id === selectedBranchForSettings);
    if (!selectedBranch) {
      return 'Sucursal no encontrada...';
    }

    let message = messageTemplate;
    message = message.replace('{nombre}', 'María');
    
    if (includeBranch) {
      message = message.replace('{sucursal}', selectedBranch.name);
      message = message.replace('{direccion}', selectedBranch.address || '');
    } else {
      message = message.replace('{sucursal}', '').replace('{direccion}', '');
    }
    
    if (includeDate) {
      message = message.replace('{fecha}', '📅 16 de octubre de 2025');
    } else {
      message = message.replace('{fecha}', '');
    }
    
    if (includeTime) {
      message = message.replace('{hora}', '🕐 2:30 PM');
    } else {
      message = message.replace('{hora}', '');
    }
    
    if (includeService) {
      // Use real service data from services list
      let serviceText = '';
      let serviceDuration = '';
      let serviceDescription = '';
      
      if (services.length > 0) {
        const exampleService = services[0];
        serviceText = exampleService.name;
        serviceDuration = exampleService.duration ? `⏱️ ${exampleService.duration}` : '';
        serviceDescription = exampleService.description || '';
      } else {
        serviceText = 'Consulta General';
        serviceDuration = '⏱️ 45 minutos';
        serviceDescription = 'Revisión médica general';
      }
      
      message = message.replace('{servicio}', serviceText);
      message = message.replace('{servicio_duracion}', serviceDuration);
      message = message.replace('{servicio_descripcion}', serviceDescription);
    } else {
      message = message.replace('{servicio}', '');
      message = message.replace('{servicio_duracion}', '');
      message = message.replace('{servicio_descripcion}', '');
    }
    
    // Clean up multiple empty lines
    message = message.replace(/\n\n\n+/g, '\n\n');
    
    return message.trim();
  };

  // Branch management functions
  const handleOpenBranchForm = (branch = null) => {
    if (branch) {
      setBranchForm(branch);
      setEditingBranch(branch);
    } else {
      setBranchForm({
        id: Date.now().toString(),
        name: '',
        address: '',
        gpsLocation: '',
        timezone: 'GMT-06:00',
        color: '#3B82F6'
      });
      setEditingBranch(null);
    }
    setShowBranchForm(true);
  };

  const handleSaveBranch = async () => {
    if (!branchForm.name.trim()) {
      toast({
        title: 'Error',
        description: 'El nombre de la sucursal es obligatorio',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingBranch) {
        // Update existing branch
        await branchesAPI.update(branchForm.id, branchForm);
        toast({
          title: 'Sucursal actualizada',
          description: 'Los cambios se guardaron correctamente',
        });
      } else {
        // Create new branch
        await branchesAPI.create(branchForm);
        toast({
          title: 'Sucursal creada',
          description: 'La sucursal se creó correctamente',
        });
      }

      // Reload branches from API
      await loadBranches();
      
      setShowBranchForm(false);
      setBranchForm({
        id: '',
        name: '',
        address: '',
        gpsLocation: '',
        timezone: 'GMT-06:00',
        color: '#3B82F6'
      });
    } catch (error) {
      console.error('Error saving branch:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la sucursal',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (window.confirm('¿Estás seguro de eliminar esta sucursal? Las citas asociadas no se eliminarán.')) {
      try {
        await branchesAPI.delete(branchId);
        await loadBranches();
        
        toast({
          title: 'Sucursal eliminada',
          description: 'La sucursal se eliminó correctamente',
        });
      } catch (error) {
        console.error('Error deleting branch:', error);
        toast({
          title: 'Error',
          description: 'No se pudo eliminar la sucursal',
          variant: 'destructive',
        });
      }
    }
  };

  // Services functions
  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm(service);
    setShowServiceForm(true);
  };

  const handleSaveService = async () => {
    if (!serviceForm.name.trim()) {
      toast({
        title: 'Error',
        description: 'El nombre del servicio es obligatorio',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingService) {
        // Update existing service
        await servicesAPI.update(serviceForm.id, serviceForm);
        toast({
          title: 'Servicio actualizado',
          description: 'Los cambios se guardaron correctamente',
        });
      } else {
        // Create new service
        await servicesAPI.create(serviceForm);
        toast({
          title: 'Servicio creado',
          description: 'El servicio se creó correctamente',
        });
      }

      // Reload services from API
      await loadServices();
      
      setShowServiceForm(false);
      setEditingService(null);
      setServiceForm({
        id: '',
        name: '',
        description: '',
        duration: '',
        color: '#10B981'
      });
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el servicio',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('¿Estás seguro de eliminar este servicio?')) {
      try {
        await servicesAPI.delete(serviceId);
        await loadServices();
        
        toast({
          title: 'Servicio eliminado',
          description: 'El servicio se eliminó correctamente',
        });
      } catch (error) {
        console.error('Error deleting service:', error);
        toast({
          title: 'Error',
          description: 'No se pudo eliminar el servicio',
          variant: 'destructive',
        });
      }
    }
  };

  const timezones = [
    { value: 'GMT-12:00', label: 'GMT-12:00 (Línea Internacional de Cambio de Fecha)' },
    { value: 'GMT-11:00', label: 'GMT-11:00 (Samoa)' },
    { value: 'GMT-10:00', label: 'GMT-10:00 (Hawái)' },
    { value: 'GMT-09:00', label: 'GMT-09:00 (Alaska)' },
    { value: 'GMT-08:00', label: 'GMT-08:00 (Pacífico - Los Ángeles)' },
    { value: 'GMT-07:00', label: 'GMT-07:00 (Montaña - Denver, Arizona)' },
    { value: 'GMT-06:00', label: 'GMT-06:00 (Central - México, Chicago)' },
    { value: 'GMT-05:00', label: 'GMT-05:00 (Este - Nueva York, Colombia, Perú)' },
    { value: 'GMT-04:00', label: 'GMT-04:00 (Venezuela, Bolivia)' },
    { value: 'GMT-03:00', label: 'GMT-03:00 (Argentina, Brasil)' },
    { value: 'GMT-02:00', label: 'GMT-02:00 (Atlántico Sur)' },
    { value: 'GMT-01:00', label: 'GMT-01:00 (Azores)' },
    { value: 'GMT+00:00', label: 'GMT+00:00 (Londres, Lisboa)' },
    { value: 'GMT+01:00', label: 'GMT+01:00 (Madrid, París, Roma)' },
    { value: 'GMT+02:00', label: 'GMT+02:00 (Atenas, El Cairo)' },
    { value: 'GMT+03:00', label: 'GMT+03:00 (Moscú, Arabia Saudita)' },
    { value: 'GMT+04:00', label: 'GMT+04:00 (Dubái)' },
    { value: 'GMT+05:00', label: 'GMT+05:00 (Pakistán)' },
    { value: 'GMT+05:30', label: 'GMT+05:30 (India)' },
    { value: 'GMT+06:00', label: 'GMT+06:00 (Bangladesh)' },
    { value: 'GMT+07:00', label: 'GMT+07:00 (Bangkok, Jakarta)' },
    { value: 'GMT+08:00', label: 'GMT+08:00 (China, Singapur)' },
    { value: 'GMT+09:00', label: 'GMT+09:00 (Japón, Corea)' },
    { value: 'GMT+10:00', label: 'GMT+10:00 (Australia Oriental)' },
    { value: 'GMT+11:00', label: 'GMT+11:00 (Islas Salomón)' },
    { value: 'GMT+12:00', label: 'GMT+12:00 (Nueva Zelanda, Fiji)' }
  ];

  const handleSave = async () => {
    try {
      console.log('[UserProfile] Saving profile to backend...', profile);
      
      // Save to backend API
      const response = await profileAPI.update(profile);
      
      console.log('[UserProfile] Profile saved successfully:', response.data);
      
      toast({
        title: '¡Perfil actualizado!',
        description: 'Tus datos se guardaron correctamente y sincronizados en todos tus dispositivos',
      });
      if (onClose) onClose();
    } catch (error) {
      console.error('[UserProfile] Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el perfil. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const getInitials = () => {
    const name = profile.name || currentUser?.name || 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-t-lg">
            <CardTitle className="text-2xl">Perfil de Usuario</CardTitle>
            <CardDescription className="text-blue-50">
              Personaliza tu perfil y configuración
            </CardDescription>
            
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                variant={activeTab === 'profile' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('profile')}
                className={`flex items-center whitespace-nowrap ${activeTab === 'profile' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/20'}`}
              >
                <User className="w-4 h-4 mr-1.5" />
                <span className="text-xs sm:text-sm">Perfil</span>
              </Button>
              <Button
                variant={activeTab === 'branches' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('branches')}
                className={`flex items-center whitespace-nowrap ${activeTab === 'branches' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/20'}`}
              >
                <Building2 className="w-4 h-4 mr-1.5" />
                <span className="text-xs sm:text-sm">Sucursales</span>
              </Button>
              <Button
                variant={activeTab === 'services' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('services')}
                className={`flex items-center whitespace-nowrap ${activeTab === 'services' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/20'}`}
              >
                <FileText className="w-4 h-4 mr-1.5" />
                <span className="text-xs sm:text-sm">Servicios</span>
              </Button>
              <Button
                variant={activeTab === 'settings' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('settings')}
                className={`flex items-center whitespace-nowrap ${activeTab === 'settings' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/20'}`}
              >
                <Settings className="w-4 h-4 mr-1.5" />
                <span className="text-xs sm:text-sm">Configuración</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            
            {/* Profile Tab */}
            {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Photo Upload Section */}
              <div className="flex flex-col items-center space-y-4">
                <Avatar className="w-32 h-32 border-4 border-blue-500">
                  {photoPreview ? (
                    <AvatarImage src={photoPreview} alt="Profile" />
                  ) : (
                    <AvatarFallback className="text-3xl bg-gradient-to-br from-blue-500 to-green-500 text-white">
                      {getInitials()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <Label htmlFor="photo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => document.getElementById('photo-upload').click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Cambiar foto
                    </Button>
                  </Label>
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Nombre completo
                  </Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Ej: Dr. Juan Pérez"
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="speciality">
                    Especialidad
                  </Label>
                  <Input
                    id="speciality"
                    value={profile.speciality}
                    onChange={(e) => handleChange('speciality', e.target.value)}
                    placeholder="Ej: Fisioterapia, Nutrición, etc."
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessName" className="flex items-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    Nombre del negocio
                  </Label>
                  <Input
                    id="businessName"
                    value={profile.businessName}
                    onChange={(e) => handleChange('businessName', e.target.value)}
                    placeholder="Ej: Clínica San Rafael"
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    Teléfono de contacto
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+34 612 345 678"
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar cambios
                </Button>
                {onClose && (
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outline"
                    className="flex-1 py-6"
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
            )}

            {/* Branches Tab */}
            {activeTab === 'branches' && (
            <div className="space-y-6">
              {/* Header with Create Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">Mis Sucursales</h3>
                  <p className="text-sm text-gray-600">Gestiona tus diferentes ubicaciones</p>
                </div>
                <Button
                  onClick={() => handleOpenBranchForm()}
                  className="bg-gradient-to-r from-blue-500 to-green-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Sucursal
                </Button>
              </div>

              {/* Branch Form */}
              {showBranchForm && (
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="branch-name">Nombre de la Sucursal *</Label>
                      <Input
                        id="branch-name"
                        value={branchForm.name}
                        onChange={(e) => setBranchForm({...branchForm, name: e.target.value})}
                        placeholder="Ej: Clínica Centro"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="branch-address">Dirección</Label>
                      <Textarea
                        id="branch-address"
                        value={branchForm.address}
                        onChange={(e) => setBranchForm({...branchForm, address: e.target.value})}
                        placeholder="Calle Principal #123, Colonia..."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="branch-gps">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        Ubicación GPS (Google Maps Link)
                      </Label>
                      <Input
                        id="branch-gps"
                        value={branchForm.gpsLocation}
                        onChange={(e) => setBranchForm({...branchForm, gpsLocation: e.target.value})}
                        placeholder="https://maps.google.com/..."
                      />
                      <p className="text-xs text-gray-500">
                        Abre Google Maps, busca tu ubicación, y copia el enlace
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="branch-timezone">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Zona Horaria
                      </Label>
                      <select
                        value={branchForm.timezone}
                        onChange={(e) => setBranchForm({...branchForm, timezone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        {timezones.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="branch-color">Color Distintivo</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          id="branch-color"
                          type="color"
                          value={branchForm.color}
                          onChange={(e) => setBranchForm({...branchForm, color: e.target.value})}
                          className="h-10 w-20 rounded cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">
                          Este color se usará para identificar las citas de esta sucursal
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={handleSaveBranch}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-green-500"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                      <Button
                        onClick={() => setShowBranchForm(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Branches List */}
              <div className="space-y-3">
                {branches.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 mb-2">No tienes sucursales creadas</p>
                    <p className="text-sm text-gray-500">
                      Crea tu primera sucursal para comenzar a organizar tus citas por ubicación
                    </p>
                  </Card>
                ) : (
                  branches.map(branch => (
                    <Card key={branch.id} className="border-l-4" style={{borderLeftColor: branch.color}}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{backgroundColor: branch.color}}
                              />
                              {branch.name}
                            </h4>
                            {branch.address && (
                              <p className="text-sm text-gray-600 mt-1">
                                📍 {branch.address}
                              </p>
                            )}
                            {branch.gpsLocation && (
                              <a
                                href={branch.gpsLocation}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                              >
                                <MapPin className="w-3 h-3" />
                                Ver en Google Maps
                              </a>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              🕐 {branch.timezone}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenBranchForm(branch)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteBranch(branch.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {onClose && (
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  className="w-full"
                >
                  Cerrar
                </Button>
              )}
            </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Gestión de Servicios</h3>
                  <p className="text-sm text-gray-600">Define los servicios que ofreces</p>
                </div>
                {!showServiceForm && (
                  <Button
                    onClick={() => {
                      setEditingService(null);
                      setServiceForm({
                        id: '',
                        name: '',
                        description: '',
                        duration: '',
                        color: '#10B981'
                      });
                      setShowServiceForm(true);
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Servicio
                  </Button>
                )}
              </div>

              {showServiceForm ? (
                <Card className="border-2 border-green-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="service-name">Nombre del Servicio *</Label>
                      <Input
                        id="service-name"
                        value={serviceForm.name}
                        onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                        placeholder="Ej: Consulta General, Fisioterapia..."
                      />
                    </div>

                    <div>
                      <Label htmlFor="service-description">Descripción (opcional)</Label>
                      <Textarea
                        id="service-description"
                        value={serviceForm.description}
                        onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                        placeholder="Breve descripción del servicio"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="service-duration">Duración aproximada</Label>
                      <Input
                        id="service-duration"
                        type="text"
                        value={serviceForm.duration}
                        onChange={(e) => setServiceForm({...serviceForm, duration: e.target.value})}
                        placeholder="Ej: 30 min aprox, 1 hora, 45 minutos..."
                      />
                    </div>

                    <div>
                      <Label htmlFor="service-color">Color identificador</Label>
                      <div className="flex gap-3">
                        <Input
                          id="service-color"
                          type="color"
                          value={serviceForm.color}
                          onChange={(e) => setServiceForm({...serviceForm, color: e.target.value})}
                          className="w-20 h-10"
                        />
                        <Input
                          value={serviceForm.color}
                          onChange={(e) => setServiceForm({...serviceForm, color: e.target.value})}
                          placeholder="#10B981"
                          className="flex-1"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={() => {
                          setShowServiceForm(false);
                          setEditingService(null);
                        }}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveService}
                        className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {loadingServices ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Cargando servicios...</p>
                    </div>
                  ) : services.length === 0 ? (
                    <Card className="border-dashed border-2">
                      <CardContent className="pt-6 pb-6 text-center">
                        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-600 mb-2">No hay servicios configurados</p>
                        <p className="text-sm text-gray-500">Crea tu primer servicio para comenzar</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-4">
                      {services.map(service => (
                        <Card key={service.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div 
                                  className="w-4 h-4 rounded-full mt-1"
                                  style={{backgroundColor: service.color}}
                                />
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900">{service.name}</h4>
                                  {service.description && (
                                    <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                                  )}
                                  {service.duration && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      {service.duration}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditService(service)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteService(service.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Dark Mode Section */}
              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {darkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                    Tema de la Aplicación
                  </CardTitle>
                  <CardDescription>
                    Cambia entre tema claro y oscuro
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">Tema Oscuro</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Reduce el brillo de la pantalla</p>
                    </div>
                    <Switch
                      checked={darkMode}
                      onCheckedChange={handleToggleDarkMode}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Auto Reminders Section */}
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Recordatorios Automáticos
                  </CardTitle>
                  <CardDescription>
                    Configura cuándo quieres que te recordemos enviar las notificaciones
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-900 font-medium mb-2">
                      📱 La app te notificará cuando sea hora de enviar recordatorios
                    </p>
                    <p className="text-xs text-orange-700">
                      Recibirás una notificación del navegador y verás un badge en el dashboard con los recordatorios pendientes
                    </p>
                  </div>

                  {/* 24 hours before */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">24 horas antes</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Recordar el día anterior</p>
                      </div>
                      <Switch
                        checked={autoReminders.enabled24h}
                        onCheckedChange={async (checked) => {
                          const newSettings = {...autoReminders, enabled24h: checked};
                          setAutoReminders(newSettings);
                          // Save to backend
                          const updatedProfile = { ...profile, autoReminders: newSettings };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>
                    {autoReminders.enabled24h && (
                      <div className="pt-2">
                        <Label htmlFor="time24h" className="text-xs">Hora del recordatorio:</Label>
                        <Input
                          id="time24h"
                          type="time"
                          value={autoReminders.time24h}
                          onChange={async (e) => {
                            const newSettings = {...autoReminders, time24h: e.target.value};
                            setAutoReminders(newSettings);
                            // Save to backend
                            const updatedProfile = { ...profile, autoReminders: newSettings };
                            await profileAPI.update(updatedProfile);
                            setProfile(updatedProfile);
                          }}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>

                  {/* 12 hours before */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">12 horas antes</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Medio día antes de la cita</p>
                      </div>
                      <Switch
                        checked={autoReminders.enabled12h}
                        onCheckedChange={async (checked) => {
                          const newSettings = {...autoReminders, enabled12h: checked};
                          setAutoReminders(newSettings);
                          // Save to backend
                          const updatedProfile = { ...profile, autoReminders: newSettings };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>
                  </div>

                  {/* Same day */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Mismo día de la cita</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Por la mañana del mismo día</p>
                      </div>
                      <Switch
                        checked={autoReminders.enabledSameDay}
                        onCheckedChange={async (checked) => {
                          const newSettings = {...autoReminders, enabledSameDay: checked};
                          setAutoReminders(newSettings);
                          // Save to backend
                          const updatedProfile = { ...profile, autoReminders: newSettings };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>
                    {autoReminders.enabledSameDay && (
                      <div className="pt-2">
                        <Label htmlFor="timeSameDay" className="text-xs">Hora del recordatorio:</Label>
                        <Input
                          id="timeSameDay"
                          type="time"
                          value={autoReminders.timeSameDay}
                          onChange={async (e) => {
                            const newSettings = {...autoReminders, timeSameDay: e.target.value};
                            setAutoReminders(newSettings);
                            // Save to backend
                            const updatedProfile = { ...profile, autoReminders: newSettings };
                            await profileAPI.update(updatedProfile);
                            setProfile(updatedProfile);
                          }}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleSaveAutoReminders}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Horarios
                  </Button>
                </CardContent>
              </Card>

              {/* Confirmation Message Template Section */}
              <Card className="border-l-4 border-l-cyan-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Plantilla de Confirmación de Cita
                  </CardTitle>
                  <CardDescription>
                    Mensaje que se enviará al paciente al agendar una cita
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="text-sm text-cyan-900 font-medium mb-2">
                      💬 Este mensaje se enviará automáticamente cuando hagas clic en "Enviar Confirmación"
                    </p>
                    <p className="text-xs text-cyan-700">
                      Puedes personalizar el mensaje y decidir qué información incluir
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="confirmation-template" className="text-base font-semibold mb-2 block">
                      Mensaje de Confirmación
                    </Label>
                    <p className="text-xs text-gray-600 mb-2">
                      Variables disponibles:{' '}
                      <Badge variant="outline">{'{nombre}'}</Badge>{' '}
                      <Badge variant="outline">{'{fecha}'}</Badge>{' '}
                      <Badge variant="outline">{'{hora}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio_duracion}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio_descripcion}'}</Badge>
                    </p>
                    <Textarea
                      id="confirmation-template"
                      value={confirmationTemplate}
                      onChange={(e) => setConfirmationTemplate(e.target.value)}
                      rows={10}
                      className="font-mono text-sm"
                      placeholder="Escribe tu mensaje de confirmación aquí..."
                    />
                  </div>

                  {/* Options */}
                  <div className="space-y-3 p-4 bg-cyan-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                    <p className="font-semibold text-sm text-cyan-900 dark:text-cyan-300">Incluir en el mensaje:</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Tipo de servicio</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: Consulta General</p>
                      </div>
                      <Switch
                        checked={confirmIncludeService}
                        onCheckedChange={async (checked) => {
                          setConfirmIncludeService(checked);
                          // Auto-save
                          const updatedProfile = {
                            ...profile,
                            confirmationTemplate,
                            confirmationIncludeBusiness: confirmIncludeBusiness,
                            confirmationIncludeSpecialty: confirmIncludeSpecialty,
                            confirmationIncludeService: checked,
                            confirmationIncludeAddress: confirmIncludeAddress,
                            confirmationIncludeReminderNote: confirmIncludeReminderNote
                          };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Nombre del negocio</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: Clínica San Rafael</p>
                      </div>
                      <Switch
                        checked={confirmIncludeBusiness}
                        onCheckedChange={async (checked) => {
                          setConfirmIncludeBusiness(checked);
                          // Auto-save
                          const updatedProfile = {
                            ...profile,
                            confirmationTemplate,
                            confirmationIncludeBusiness: checked,
                            confirmationIncludeSpecialty: confirmIncludeSpecialty,
                            confirmationIncludeService: confirmIncludeService,
                            confirmationIncludeAddress: confirmIncludeAddress,
                            confirmationIncludeReminderNote: confirmIncludeReminderNote
                          };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Especialidad</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: Fisioterapia</p>
                      </div>
                      <Switch
                        checked={confirmIncludeSpecialty}
                        onCheckedChange={async (checked) => {
                          setConfirmIncludeSpecialty(checked);
                          // Auto-save
                          const updatedProfile = {
                            ...profile,
                            confirmationTemplate,
                            confirmationIncludeBusiness: confirmIncludeBusiness,
                            confirmationIncludeSpecialty: checked,
                            confirmationIncludeService: confirmIncludeService,
                            confirmationIncludeAddress: confirmIncludeAddress,
                            confirmationIncludeReminderNote: confirmIncludeReminderNote
                          };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Dirección de la sucursal</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Incluye nombre y ubicación</p>
                      </div>
                      <Switch
                        checked={confirmIncludeAddress}
                        onCheckedChange={async (checked) => {
                          setConfirmIncludeAddress(checked);
                          // Auto-save
                          const updatedProfile = {
                            ...profile,
                            confirmationTemplate,
                            confirmationIncludeBusiness: confirmIncludeBusiness,
                            confirmationIncludeSpecialty: confirmIncludeSpecialty,
                            confirmationIncludeService: confirmIncludeService,
                            confirmationIncludeAddress: checked,
                            confirmationIncludeReminderNote: confirmIncludeReminderNote
                          };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between border-t dark:border-gray-700 pt-3">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Nota de recordatorio futuro</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">"Recibirás un recordatorio..."</p>
                      </div>
                      <Switch
                        checked={confirmIncludeReminderNote}
                        onCheckedChange={async (checked) => {
                          setConfirmIncludeReminderNote(checked);
                          // Auto-save
                          const updatedProfile = {
                            ...profile,
                            confirmationTemplate,
                            confirmationIncludeBusiness: confirmIncludeBusiness,
                            confirmationIncludeSpecialty: confirmIncludeSpecialty,
                            confirmationIncludeService: confirmIncludeService,
                            confirmationIncludeAddress: confirmIncludeAddress,
                            confirmationIncludeReminderNote: checked
                          };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-sm text-green-900 mb-2">Vista previa:</p>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono break-words overflow-wrap-anywhere">
                      {getConfirmationPreview()}
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveConfirmationTemplate}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Plantilla de Confirmación
                  </Button>
                </CardContent>
              </Card>

              {/* Reminder Message Template Section */}
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Plantilla de Mensaje de Recordatorio
                  </CardTitle>
                  <CardDescription>
                    Mensaje que se enviará al recordar una cita (botón "Enviar" o recordatorios automáticos)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-900 font-medium mb-2">
                      🔔 Este mensaje se usa para recordatorios de citas
                    </p>
                    <p className="text-xs text-orange-700">
                      Se agregarán automáticamente los enlaces de confirmar/cancelar
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="reminder-template" className="text-base font-semibold mb-2 block">
                      Mensaje de Recordatorio
                    </Label>
                    <p className="text-xs text-gray-600 mb-2">
                      Variables disponibles:{' '}
                      <Badge variant="outline">{'{nombre}'}</Badge>{' '}
                      <Badge variant="outline">{'{fecha}'}</Badge>{' '}
                      <Badge variant="outline">{'{hora}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio_duracion}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio_descripcion}'}</Badge>
                    </p>
                    <Textarea
                      id="reminder-template"
                      value={reminderTemplate}
                      onChange={(e) => setReminderTemplate(e.target.value)}
                      rows={10}
                      className="font-mono text-sm"
                      placeholder="Escribe tu mensaje de recordatorio aquí..."
                    />
                  </div>

                  {/* Options */}
                  <div className="space-y-3 p-4 bg-orange-50 rounded-lg">
                    <p className="font-semibold text-sm text-orange-900">Opciones:</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Incluir detalles del servicio</p>
                        <p className="text-xs text-gray-600">Duración y descripción del servicio</p>
                      </div>
                      <Switch
                        checked={reminderIncludeServiceDetails}
                        onCheckedChange={async (checked) => {
                          setReminderIncludeServiceDetails(checked);
                          // Auto-save
                          const updatedProfile = {
                            ...profile,
                            reminderTemplate,
                            reminderIncludeServiceDetails: checked
                          };
                          await profileAPI.update(updatedProfile);
                          setProfile(updatedProfile);
                        }}
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-sm text-green-900 mb-2">Vista previa:</p>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono break-words overflow-wrap-anywhere">
                      {getReminderPreview()}
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveReminderTemplate}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Plantilla de Recordatorio
                  </Button>
                </CardContent>
              </Card>

              {/* Thanks Message Template Section */}
              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Plantilla de Mensaje de Agradecimiento
                  </CardTitle>
                  <CardDescription>
                    Mensaje que se enviará después de la visita del paciente (opción en menú de tres puntos)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-900 font-medium mb-2">
                      💜 Este mensaje se usa para agradecer después de la visita
                    </p>
                    <p className="text-xs text-purple-700">
                      Puedes incluir enlaces a redes sociales y para calificar el servicio
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="thanks-template" className="text-base font-semibold mb-2 block">
                      Mensaje de Agradecimiento
                    </Label>
                    <p className="text-xs text-gray-600 mb-2">
                      Variables disponibles:{' '}
                      <Badge variant="outline">{'{nombre}'}</Badge>{' '}
                      <Badge variant="outline">{'{redes_sociales}'}</Badge>{' '}
                      <Badge variant="outline">{'{link_calificacion}'}</Badge>
                    </p>
                    <Textarea
                      id="thanks-template"
                      value={thanksTemplate}
                      onChange={(e) => setThanksTemplate(e.target.value)}
                      rows={8}
                      className="font-mono text-sm"
                      placeholder="Escribe tu mensaje de agradecimiento aquí..."
                    />
                  </div>

                  {/* Options */}
                  <div className="space-y-3 p-4 bg-purple-50 rounded-lg">
                    <p className="font-semibold text-sm text-purple-900">Opciones:</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Incluir redes sociales</p>
                        <p className="text-xs text-gray-600">Links de Facebook, Instagram y WhatsApp</p>
                      </div>
                      <Switch
                        checked={thanksIncludeSocial}
                        onCheckedChange={setThanksIncludeSocial}
                      />
                    </div>

                    {thanksIncludeSocial && (
                      <div className="ml-4 space-y-2">
                        <div>
                          <Label htmlFor="facebook-link" className="text-xs">Facebook</Label>
                          <Input
                            id="facebook-link"
                            type="text"
                            value={socialLinks.facebook}
                            onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                            placeholder="https://facebook.com/tu-pagina"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="instagram-link" className="text-xs">Instagram</Label>
                          <Input
                            id="instagram-link"
                            type="text"
                            value={socialLinks.instagram}
                            onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                            placeholder="https://instagram.com/tu-cuenta"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="whatsapp-link" className="text-xs">WhatsApp Business</Label>
                          <Input
                            id="whatsapp-link"
                            type="text"
                            value={socialLinks.whatsapp}
                            onChange={(e) => setSocialLinks({...socialLinks, whatsapp: e.target.value})}
                            placeholder="https://wa.me/521234567890"
                            className="text-sm"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Incluir link de calificación</p>
                        <p className="text-xs text-gray-600">Link para que califiquen el servicio</p>
                      </div>
                      <Switch
                        checked={thanksIncludeReview}
                        onCheckedChange={setThanksIncludeReview}
                      />
                    </div>

                    {thanksIncludeReview && (
                      <div className="ml-4">
                        <Label htmlFor="review-link" className="text-xs">Link de Calificación</Label>
                        <Input
                          id="review-link"
                          type="text"
                          value={reviewLink}
                          onChange={(e) => setReviewLink(e.target.value)}
                          placeholder="https://g.page/tu-negocio/review"
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-sm text-green-900 mb-2">Vista previa:</p>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono break-words overflow-wrap-anywhere">
                      {getThanksPreview()}
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveThanksTemplate}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Plantilla de Agradecimiento
                  </Button>
                </CardContent>
              </Card>

              {/* Message Template Section */}
              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Plantilla de Recordatorios por Sucursal
                  </CardTitle>
                  <CardDescription>
                    Personaliza el mensaje de tus recordatorios para cada sucursal
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Branch Selector */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label htmlFor="branch-select" className="text-base font-semibold mb-2 block">
                      1. Selecciona una Sucursal
                    </Label>
                    {branches.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-600 mb-2">
                          No tienes sucursales creadas
                        </p>
                        <Button
                          size="sm"
                          onClick={() => setActiveTab('branches')}
                          variant="outline"
                        >
                          Ir a crear sucursal
                        </Button>
                      </div>
                    ) : (
                      <select
                        value={selectedBranchForSettings || ''}
                        onChange={(e) => setSelectedBranchForSettings(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Selecciona una sucursal...</option>
                        {branches.map(branch => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Template Editor - Only show if branch is selected */}
                  {selectedBranchForSettings && (
                  <>
                  <div>
                    <Label htmlFor="template" className="text-base font-semibold mb-2 block">
                      2. Personaliza tu mensaje
                    </Label>
                    <p className="text-xs text-gray-600 mb-2">
                      Variables disponibles:{' '}
                      <Badge variant="outline">{'{nombre}'}</Badge>{' '}
                      <Badge variant="outline">{'{sucursal}'}</Badge>{' '}
                      <Badge variant="outline">{'{direccion}'}</Badge>{' '}
                      <Badge variant="outline">{'{fecha}'}</Badge>{' '}
                      <Badge variant="outline">{'{hora}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio_duracion}'}</Badge>{' '}
                      <Badge variant="outline">{'{servicio_descripcion}'}</Badge>
                    </p>
                    <Textarea
                      id="template"
                      value={messageTemplate}
                      onChange={(e) => setMessageTemplate(e.target.value)}
                      rows={8}
                      className="font-mono text-sm"
                      placeholder="Escribe tu mensaje aquí..."
                    />
                  </div>

                  {/* Options */}
                  <div className="space-y-3 p-4 bg-blue-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                    <p className="font-semibold text-sm text-blue-900 dark:text-blue-300">3. Incluir en el mensaje:</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Fecha de la cita</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: 📅 16 de octubre de 2025</p>
                      </div>
                      <Switch
                        checked={includeDate}
                        onCheckedChange={setIncludeDate}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Hora de la cita</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: 🕐 2:30 PM</p>
                      </div>
                      <Switch
                        checked={includeTime}
                        onCheckedChange={setIncludeTime}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Tipo de servicio</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: 📋 Consulta General</p>
                      </div>
                      <Switch
                        checked={includeService}
                        onCheckedChange={setIncludeService}
                      />
                    </div>

                    <div className="flex items-center justify-between border-t dark:border-gray-700 pt-3">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Nombre y dirección de la sucursal</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ejemplo: Clínica Centro - Calle Principal #123</p>
                      </div>
                      <Switch
                        checked={includeBranch}
                        onCheckedChange={setIncludeBranch}
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-sm text-green-900 mb-2">Vista previa:</p>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono break-words overflow-wrap-anywhere">
                      {getPreviewMessage()}
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveSettings}
                    className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white"
                    disabled={!selectedBranchForSettings}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Configuración de esta Sucursal
                  </Button>
                  </>
                  )}
                </CardContent>
              </Card>

              {/* Back Button */}
              {onClose && (
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  className="w-full"
                >
                  Cerrar
                </Button>
              )}
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserProfile;